import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import LaunchScreen from './components/LaunchScreen';
import PageTransitionLayout from './components/PageTransitionLayout';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import ScrollToTop from './components/ScrollToTop';
import { MAIN_TOP_PADDING } from './constants/layout';

// Components
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import TrustStrip from './components/Layout/TrustStrip';
import AnalyticsTracker from './components/AnalyticsTracker';
import ChatWidget from './components/ChatWidget';
import ConversionLeadBar from './components/ConversionLeadBar';

// Pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Portfolio from './pages/Portfolio';
import AISolutions from './pages/AISolutions';
import Contact from './pages/Contact';
import RequestQuote from './pages/RequestQuote';
import Blogs from './pages/Blogs';
import Courses from './pages/Courses';
import Careers from './pages/Careers';
import GenericPage from './pages/GenericPage';

// Theme — professional, calm, high-trust (not “startup flashy”)
const theme = createTheme({
  palette: {
    primary: {
      main: '#1D1D4E',
      light: '#2D2D6E',
      dark: '#12133A',
    },
    secondary: {
      main: '#0E8BC7',
      light: '#3BA3D9',
      dark: '#0A6E9E',
    },
    text: {
      primary: '#0f172a',
      secondary: '#475569',
    },
    background: {
      default: '#FAFBFC',
      paper: '#FFFFFF',
    },
    divider: 'rgba(15, 23, 42, 0.08)',
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: 'clamp(2rem, 4vw, 3.25rem)',
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 700,
      fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
      lineHeight: 1.2,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontWeight: 700,
      fontSize: 'clamp(1.35rem, 2vw, 1.75rem)',
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
    },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.6 },
    button: { fontWeight: 600, letterSpacing: '0.01em' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 22px',
          fontSize: '0.9375rem',
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 2px 8px rgba(29, 29, 78, 0.12)' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid rgba(15, 23, 42, 0.06)',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            transform: 'none',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
            borderColor: 'rgba(14, 139, 199, 0.25)',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router basename="/">
        <LaunchScreen />
        <ScrollToTop />
        <AnalyticsTracker />
        <div className="App">
          <Navbar />
          <Box
            component="main"
            sx={{
              pt: MAIN_TOP_PADDING,
              pb: '52px',
              minHeight: '100vh',
            }}
          >
            <Routes>
              <Route element={<PageTransitionLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/ai-solutions" element={<AISolutions />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/request-quote" element={<RequestQuote />} />
                <Route path="/blogs" element={<Blogs />} />
                <Route path="/blogs/:slug" element={<Blogs />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:slug" element={<Courses />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/page/:slug" element={<GenericPage />} />
              </Route>
            </Routes>
          </Box>
          <TrustStrip />
          <Footer />
          <ConversionLeadBar />
          <ChatWidget />
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;